/* eslint-disable no-console */
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { FOLDER, RENDERING_CONTEXT, SEARCH } from '@bedrock-rdk/constants';
import { featureFlipping } from '@bedrock-rdk/config';
import { getLayoutTheme } from '../tornado/utils/theme';
import { TRANSPARENT } from '../tornado/utils/colors';
import { GRID_POPUP_ID } from '../popups/popups.constants';
import { handleInteraction } from '../app/utils/app.utils';
import { isImpressionTrackingEnabled, track, untrack } from '../tornado/utils/impression';
import { getContentTemplateByTemplateId, getContentSizeByContentTemplateId } from '../tornado/templates';
import {
  PORTION_REQUESTED_SIGNAL,
  SELECTED_SIGNAL,
  EPG_SIGNAL,
  HIDE_BACKGROUND_SIGNAL,
  SHOW_BACKGROUND_SIGNAL,
  SHOW_GRID_SIGNAL,
  IMPRESSION_UNTRACK_SIGNAL,
  IMPRESSION_TRACK_SIGNAL,
} from '../tornado/signals';
import { fetchBlockPage, fetchEPGLayout, toggleFacet } from '../../logic/layout/layout.actions';
import { showPopupAction } from '../../logic/popup/popup.actions';
import { ContextTitle } from '../contextTitle/contextTitle.component';
import { RECTANGLE_HEIGHT } from '../contextTitle/contextTitle.style';
import { FOCUS_MODE_CENTER, FOCUS_MODE_LEFT } from '../tornado/row/row.constants';
import {
  calculateBlockHeight,
  getBlockTemplateType,
  getStableBlockId,
  makeBlockViewKey,
  shouldDisplayContextTitle,
  shouldDisplayShowMoreButtons,
} from './abstractLayout.utils';
import {
  getInitialStyle,
  defaultContext,
  WIDTH,
  HEIGHT,
  BACKGROUND_COLOR,
  getContentPadding,
  BOTTOM_PADDING,
} from './abstractLayout.style';
import connect from './abstractLayout.connect';
import { FacetRow } from '../tornado/facet-row/facetRow.component';

export class AbstractLayout extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
        },
        ContextTitle: {
          type: ContextTitle,
        },
        Blocks: {},
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._positions = [];
    this._heights = [];
    this._indexMap = [];
    this._rowBlockIds = [];
    this._index = 0;
    this._offset = 0;
    this._hasSideNav = false;
    this._style = defaultContext;
    this._verticalOffset = 0;
    this._viewsCount = 0;
    this._viewportWidth = WIDTH[defaultContext.screenSize];
    this._viewportHeight = HEIGHT[defaultContext.screenSize];
    this._backgroundColor = BACKGROUND_COLOR[defaultContext.colorMode];
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _active() {
    this._applyBackdrop();
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this._data = undefined;
  }

  /**
   * Builds a lookup of the block views that could serve the incoming layout again.
   *
   * Keyed on the block identity plus both template ids, since a view can only be handed a block it is able to
   * render: the Lightning class is fixed at creation, and a Row reuses its pooled item views by index without
   * re-checking their type, so a different content template has to start from a fresh view.
   *
   * @returns {Map} a `key -> view` lookup
   */
  _collectReusableBlockViews() {
    const reusable = new Map();
    const views = this.tag('Blocks').children;

    for (let i = 0; i < views.length; i += 1) {
      const view = views[i];
      const { data } = view;

      if (data && data.block && data.block.id !== undefined) {
        const key = makeBlockViewKey(data.blockTemplate, data.itemTemplate, data.block.id);
        const candidates = reusable.get(key);

        // A layout may hold the same block under the same templates more than once
        if (candidates) {
          candidates.push(view);
        } else {
          reusable.set(key, [view]);
        }
      }
    }

    return reusable;
  }

  get data() {
    return this._data;
  }

  set data(value) {
    const { blocks, entity, layout } = value;
    const isSearchPage = entity && entity.id === SEARCH;
    const renderingContext = isSearchPage ? RENDERING_CONTEXT.SEARCH : RENDERING_CONTEXT.GENERAL;

    const contentPadding = getContentPadding(this._hasSideNav);
    const currentViews = this.tag('Blocks').children;
    // NOTE: the views are matched to the incoming blocks by identity rather than by position, so a layout that
    // adds, drops or reorders blocks keeps the views it can and only pays for what actually changed. The geometry
    // is rebuilt either way, which is what a positional reuse used to get wrong
    const reusableViews = this._collectReusableBlockViews();
    const previousFacetRow = currentViews[0] instanceof FacetRow ? currentViews[0] : null;

    this._data = value;
    this._hasFacets = Boolean(value.facets?.length);
    this._positions = [];
    this._heights = [];
    this._indexMap = [];
    this._rowBlockIds = [];

    const focusMode = featureFlipping.leftCornerFocus ? FOCUS_MODE_LEFT : FOCUS_MODE_CENTER;
    const theme = getLayoutTheme(_get(layout, 'theme'));
    const { screenSize } = this._style;
    const children = [];
    const width = this._viewportWidth;
    const halfOfScreenHeight = this._viewportHeight / 2;
    const displayShowMore = shouldDisplayShowMoreButtons(entity);

    let offset = 0;
    const facetIndexOffset = this._hasFacets ? 1 : 0;
    if (this._hasFacets) {
      const facetOffset = FacetRow.getOffsetsByBlockData({ screenSize, renderingContext });
      const facetHeight = facetOffset.h + facetOffset.top + facetOffset.bottom;
      const facetData = {
        items: value.facets,
        renderingContext,
      };

      if (previousFacetRow) {
        previousFacetRow.patch({ data: facetData, contentPadding, w: width, h: facetHeight });
        children.push(previousFacetRow);
      } else {
        children.push({
          type: FacetRow,
          w: width,
          h: facetHeight,
          contentPadding,
          data: facetData,
          signals: {
            [SELECTED_SIGNAL]: '_handleFacetSelected',
          },
        });
      }

      this._positions.push(0);
      this._indexMap.push(0);
      this._rowBlockIds.push(undefined);
      offset += facetHeight;
    }

    for (let i = 0; i < blocks.length; i += 1) {
      const data = blocks[i];

      // Define Lightning classes that would be used to instantiate block and item views
      const blockType = getBlockTemplateType(data);
      const itemType = getContentTemplateByTemplateId(data.content.contentTemplateId);

      // Define block and item sizes
      const itemSize = getContentSizeByContentTemplateId(data.content.contentTemplateId, renderingContext);
      const blockSize = calculateBlockHeight({
        blockType,
        itemType,
        data,
        itemSize,
        blockIndex: i + facetIndexOffset,
        screenSize,
        displayShowMore,
        isSearchPage,
      });

      const blockData = {
        block: data,
        blockIndex: i + facetIndexOffset,
        blockTemplate: data.blockTemplateId,
        itemSize,
        itemType,
        itemTemplate: data.content.contentTemplateId,
        theme,
        rowSize: {
          w: width,
          h: blockSize.h,
        },
        displayShowMore,
        isSearchPage,
        renderingContext,
      };

      const candidates = reusableViews.get(
        makeBlockViewKey(data.blockTemplateId, data.content.contentTemplateId, data.id),
      );
      let reused = null;

      while (candidates && candidates.length && !reused) {
        const candidate = candidates.shift();

        if (candidate.constructor === blockType) {
          reused = candidate;
        }
      }

      if (reused) {
        // NOTE: the reducer preserves the identity of untouched blocks, so re-applying identical data would run the
        // whole Block/Row/item patch cascade for nothing. The position still has to be applied, a block above may
        // have changed size
        const isUnchanged =
          reused.data.block === data &&
          reused.data.blockIndex === blockData.blockIndex &&
          reused.data.rowSize.h === blockSize.h;

        reused.patch({
          ...(isUnchanged ? {} : { data: blockData }),
          focusMode,
          contentPadding,
          w: width,
          h: blockSize.h,
          y: offset,
        });
        children.push(reused);
      } else {
        children.push({
          type: blockType,
          focusMode,
          data: blockData,
          contentPadding,
          w: width,
          h: blockSize.h,
          y: offset,
          signals: {
            [SELECTED_SIGNAL]: '_handleSelected',
            [PORTION_REQUESTED_SIGNAL]: '_handlePortionRequest',
            [EPG_SIGNAL]: '_handleEpg',
            [HIDE_BACKGROUND_SIGNAL]: '_hideBackground',
            [SHOW_BACKGROUND_SIGNAL]: '_showBackground',
            [SHOW_GRID_SIGNAL]: '_showGrid',
            [IMPRESSION_TRACK_SIGNAL]: '_impressionTrack',
            [IMPRESSION_UNTRACK_SIGNAL]: '_impressionUnTrack',
          },
        });
      }

      const rowSize = Math.round(blockSize.h / blockSize.rowCount);
      const halfOfRowSize = Math.round(rowSize / 2);

      // For per-block impression tracking
      this._heights.push({
        top: offset,
        bottom: offset + blockSize.h,
        heightForImpression: Math.round(blockSize.h / 2),
      });

      for (let j = 0; j < blockSize.rowCount; j += 1) {
        this._positions.push(Math.min(halfOfScreenHeight - offset - halfOfRowSize, 0));
        this._indexMap.push(i + facetIndexOffset);
        this._rowBlockIds.push(getStableBlockId(data.id));
        offset += rowSize;
      }
    }

    this._backgroundColor = theme.backgroundColor || BACKGROUND_COLOR[this._style.colorMode];

    this._applyBackdrop();

    // Any view left unclaimed belongs to a block the new layout no longer has: assigning the array drops it
    this.patch({
      Background: {
        color: this._backgroundColor,
      },
      Blocks: {
        children,
      },
    });

    if (this._positions.length) {
      if (offset > this._viewportHeight) {
        const minOffset = this._viewportHeight - offset - BOTTOM_PADDING[this._style.screenSize];
        this._positions = this._positions.map(y => Math.max(minOffset, y));
      }

      this._setIndex(Math.min(this._resolveAnchoredIndex(), this._positions.length - 1));
    }

    const verticalOffset = this._getVerticalOffset();
    if (!verticalOffset) {
      this.tag('ContextTitle').alpha = 0;
    }

    if (verticalOffset !== this._verticalOffset) {
      this._verticalOffset = verticalOffset;
      this._setIndex(this._index);
    }

    this._refocus();
    this._restoreFocusedItemIndex();
  }

  set hasSideNav(value) {
    if (this._hasSideNav !== value) {
      this._hasSideNav = value;
      this._applyContentPadding();
    }
  }

  historyState(history) {
    if (history) {
      const { row = 0, column = 0, blockId, rowInBlock = 0 } = history;
      const index = Number.isFinite(row) ? row : 0;
      this._index = Math.max(index, 0);
      this._column = column;
      this._anchor = blockId === undefined ? undefined : { blockId, rowInBlock };
      this._setIndex(this._index);
      this._restoreFocusedItemIndex();

      return undefined;
    }

    const focusedBlock = this._getFocused();
    const blockId = this._rowBlockIds[this._index];

    return {
      row: this._index,
      column: focusedBlock ? focusedBlock.focusedIndex : 0,
      // NOTE: `row` alone only means something against the geometry it was taken from, see _resolveAnchoredIndex
      blockId,
      rowInBlock: blockId === undefined ? 0 : this._index - this._rowBlockIds.indexOf(blockId),
    };
  }

  /**
   * Resolves which row to restore once the geometry has been rebuilt.
   *
   * `_index` is a flat row counter across every block, so it only describes a position within the exact layout it
   * was taken from. A layout arriving from the network may carry a different set of blocks, or differently sized
   * ones, than the cached layout that was rendered first — which left the same number pointing at an unrelated row.
   * Anchoring on the block the user was actually on survives that.
   *
   * @returns {Number} the row index to restore
   */
  _resolveAnchoredIndex() {
    if (!this._anchor) {
      return this._index;
    }

    const { blockId, rowInBlock } = this._anchor;
    const firstRow = this._rowBlockIds.indexOf(blockId);

    if (firstRow === -1) {
      return this._index;
    }

    // The rows of a block are contiguous, so its last row bounds how far the offset can be applied
    return Math.min(firstRow + rowInBlock, this._rowBlockIds.lastIndexOf(blockId));
  }

  _applyContentPadding() {
    const value = getContentPadding(this._hasSideNav);
    this.tag('Blocks').children.forEach(child => {
      child.contentPadding = value;
    });
  }

  _getFocused() {
    const views = this.tag('Blocks').children;

    return views && views[this._indexMap[this._index]];
  }

  _handleUp() {
    if (this._index > 0) {
      const previousBlock = this._getFocused();

      this._setIndex(this._index - 1, true);

      // In case we are moving focus to grid (which is the only block type exposing maxIndex) after anchoring
      const block = this._getFocused();
      if (block && previousBlock !== block && !block.focusedIndex) {
        block.focusedIndex = block.maxIndex || 0;
      }

      return true;
    }

    return false;
  }

  _handleDown() {
    if (this._index < this._indexMap.length - 1) {
      this._setIndex(this._index + 1, true);

      return true;
    }

    return false;
  }

  _captureKey() {
    this._column = undefined;
    this._anchor = undefined;

    return false;
  }

  _restoreFocusedItemIndex() {
    const focusedBlock = this._getFocused();
    if (!focusedBlock || !Number.isFinite(this._column) || focusedBlock.focusedIndex === this._column) {
      return;
    }

    focusedBlock.focusedIndex = this._column;

    // NOTE: the focusedIndex setters only record the index, the block still has to be moved onto it — otherwise
    // the restored column stays invisible until some later data application happens to re-run the block's _setIndex
    if (typeof focusedBlock.goTo === 'function') {
      focusedBlock.goTo(focusedBlock.focusedIndex);
    }
  }

  _handleFacetSelected(value) {
    Store.dispatch(toggleFacet(value));
  }

  _handleSelected(item) {
    handleInteraction(item, this);
  }

  _handlePortionRequest(portion) {
    Store.dispatch(fetchBlockPage(portion));
  }

  _handleEpg(date) {
    Store.dispatch(fetchEPGLayout(date));
  }

  _showGrid(block) {
    Store.dispatch(showPopupAction(GRID_POPUP_ID, { block }));
  }

  _impressionTrack(blockIndex, items) {
    if (this._isBlockFullyVisible(blockIndex)) {
      track(blockIndex, items);
    }
  }

  _impressionUnTrack(blockIndex, items) {
    untrack(blockIndex, items);
  }

  _isBlockFullyVisible(blockIndex) {
    const start = this.finalY - this._offset;
    const end = this.finalH - this._offset;

    if (blockIndex === this._index) {
      // This is the currently focused block, no extra checks needed
      return true;
    }

    if (!this._heights[blockIndex]) {
      return false;
    }

    const { top, bottom, heightForImpression: h } = this._heights[blockIndex];

    if (top > end || bottom < start) {
      return false;
    }

    if (end - top < h || bottom - start < h) {
      return false;
    }

    return true;
  }

  // The framebuffer is cleared either way, so a full screen opaque rect on top of it was a second pass per pixel
  _applyBackdrop() {
    // The clear color is global, so a page left behind (e.g. under the player) must not repaint it, _active restores it
    if (!this.active) {
      return;
    }

    this.stage.setClearColor(this._backgroundHidden ? TRANSPARENT : this._backgroundColor);
  }

  _hideBackground() {
    this._backgroundHidden = true;
    const background = this.tag('Background');
    // Handing the fade to the rect as the clear turns transparent, so the same 250ms transition still plays
    background.alpha = 1;
    this._applyBackdrop();
    background.setSmooth('alpha', 0, { duration: 0.25 });
  }

  _showBackground() {
    this._backgroundHidden = false;
    this._applyBackdrop();
    this.tag('Background').alpha = 0;
  }

  _setIndex(index = this._index, shouldAnimate = false) {
    const position = this._positions[index];
    if (!Number.isFinite(position)) {
      return;
    }

    const offset = index === 0 ? this._verticalOffset : 0;
    this._offset = position + offset;
    this._index = index;

    if (shouldAnimate) {
      this.tag('Blocks').setSmooth('y', this._offset);
    } else {
      this.tag('Blocks').y = this._offset;
    }

    if (isImpressionTrackingEnabled()) {
      for (let i = 0; i < this._heights.length; i += 1) {
        if (!this._isBlockFullyVisible(i)) {
          untrack(i);
        }
      }
    }
  }

  _getVerticalOffset() {
    if (this._data) {
      const { entity } = this._data;
      if (shouldDisplayContextTitle(entity, FOLDER)) {
        this.tag('ContextTitle').patch({
          data: entity,
          alpha: 1,
        });

        return RECTANGLE_HEIGHT[this._style.screenSize];
      }
    }

    return 0;
  }
}
